import { useFragment } from '@apollo/client'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageSource, FileUsageTypeEnum, getFileUsage } from '@vedalib/editor/lib/files'

import type { FileMetaSchemaFragment, FileSchemaFragment } from 'gql/__generated__/graphql'
import { VideoResolution } from 'gql/__generated__/graphql'
import { getFileMetaByIdFromCache } from 'gql/files/apollo'
import { fileFragment } from 'gql/files/gql/fragments'

export type FilesWithPath = {
  path?: string
  file?: FileSchemaFragment

  originalFile?: FileSchemaFragment
  name?: string
  processing?: boolean
}

export const COMPANY_DEFAULT_LOGO = window.PLATFORM.companyLogo
export const DEFAULT_FAVICON = window.PLATFORM.companyLogo

export const getCompanyLogoOrDefault = (logo?: FileMetaSchemaFragment | null): string => {
  const file = logo && getFileInFileMeta(logo, 'original')
  return file?.path ? file.path : COMPANY_DEFAULT_LOGO
}

export const getFaviconOrDefault = (faviconId: string | null | undefined): string => {
  const faviconData = getOriginalImageById(faviconId)
  return faviconData?.path || DEFAULT_FAVICON
}

export const getFileInFileMeta = (
  fileMeta: FileMetaSchemaFragment,
  key = 'original',
): FilesWithPath => {
  const intFiles = fileMeta.files
  const file = intFiles.find((e) => e.key === key)
  const originalFile = intFiles.find((e) => e.key === 'original')
  return {
    processing: !fileMeta.isProcessed,
    path: file?.path,
    file: file,
    originalFile: originalFile,
    name: fileMeta.name,
  }
}

export const getVideoKey = () => {
  // В SCORM пакетах всегда используем 'original' ключ
  // (backend уже копирует сжатое видео под именем оригинального файла)
  return VideoResolution.original
}

export const getFileByUsage = (
  file: Omit<FileUsage, 'type'> | null,
  type: FileUsageTypeEnum,
): FilesWithPath | null => {
  const fileMeta = getFileMetaByIdFromCache(file?.id)
  if (!file || !fileMeta) {
    return null
  }

  const fileUsage = getFileUsage(type)(file)
  let key = fileUsage?.key || 'original'
  if (type === FileUsageTypeEnum.video) {
    key = getVideoKey()
  }
  // console.log('files', { fileUsage, type, file })
  return getFileInFileMeta(fileMeta, key)
}

export const getFileAsCommonById = (imageId: string | null | undefined): FilesWithPath | null => {
  if (!imageId) {
    return null
  }

  return getFileByUsage({ id: imageId }, FileUsageTypeEnum.common)
}

export const useFileByUsage = (
  usage: Omit<FileUsage, 'type'> | null,
  type: FileUsageTypeEnum,
): FilesWithPath | null => {
  useFragment({
    fragment: fileFragment,
    fragmentName: 'FileMetaSchema',
    from: {
      __typename: 'FileMeta',
      id: usage?.id || '',
    },
  })
  if (usage?.source === FileUsageSource.url) {
    return {
      path: usage.url,
    }
  }
  return getFileByUsage(usage, type)
}

export const useUrlByUsage = (
  usage: Omit<FileUsage, 'type'> | null,
  type: FileUsageTypeEnum,
): string | undefined => {
  const res = useFileByUsage(usage, type)
  return res?.path
}

export const getOriginalImageById = (imageId: string | null | undefined): FilesWithPath | null => {
  if (!imageId) {
    return null
  }

  return getFileByUsage({ id: imageId, source: FileUsageSource.file }, FileUsageTypeEnum.image)
}

export const getFastStartUrl = (fileMeta: FileMetaSchemaFragment) => {
  const key = getVideoKey()
  if (!fileMeta) {
    return null
  }

  const files = fileMeta.files.filter((file) => file.isProcessed && file.path)
  const keyFile = files.find((file) => file.key === key)
  if (keyFile) {
    return keyFile.path
  }
  // Fallback to fast start file if available
  const fastStartFile = files.find((file) => file.isFastStart)
  if (fastStartFile) {
    return fastStartFile.path
  }

  const originalFile = files.find((file) => file.key === 'original')
  if (originalFile) {
    return originalFile.path
  }

  return fileMeta.files[0].path || null
}

export const useVideoAndAudioUrl = (usage: Omit<FileUsage, 'type'> | null): string | null => {
  useFragment({
    fragment: fileFragment,
    fragmentName: 'FileMetaSchema',
    from: {
      __typename: 'FileMeta',
      id: usage?.id || '',
    },
  })
  if (usage?.source === FileUsageSource.url) {
    return usage.url || null
  }
  const fileMeta = getFileMetaByIdFromCache(usage?.id)
  if (!fileMeta) {
    return null
  }

  return getFastStartUrl(fileMeta)
}

export const getLinkFileFromUrl = (url: string | null | undefined) => {
  if (!url) {
    return null
  }

  const match = url.match(/url\(["']?(.*?)["']?\)/)
  if (match && match[1]) {
    return match[1]
  }
  return url
}
